'use client';

import { RoundButton } from '@/components/buttons/Buttons';
import RadioButtonInput from '@/components/form-inputs/RadioButtonInput';
import { TextBodyExtraExtraSmall } from '@/components/text/Texts';
import { TRAVEL_PROGRAM_FORM } from '@/constants/forms';
import FooterContent from '@/modules/account/components/travel-customization/FooterContent';
import HeaderContent from '@/modules/account/components/travel-customization/HeaderContent';
import { get, isEmpty } from 'lodash';
import type { DialogProps } from 'primereact/dialog';
import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { MdReceiptLong } from 'react-icons/md';
import SecondaryModal from './SecondaryModal';
import UploadExpenseCodeModal from './UploadExpenseCodeModal';
import './travel-customization.styles.css';

interface ModalProps extends DialogProps {
	setVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

const ExpensesCodeModal = (props: ModalProps) => {
	const { setVisible, ...rest } = props;

	const [showUploadExpenseCodeModal, setShowUploadExpenseCodeModal] =
		useState<boolean>(false);

	const {
		control,
		resetField,
		trigger,
		watch,
		setValue,
		formState: { errors },
	} = useFormContext();
	const isCodeRequired = watch('expenseCodeType') === 'required';

	const uploadExpenseCode = (
		<div className="mt-6">
			<div className="flex justify-between items-center px-4">
				<span className="text-base">Upload expense codes</span>
				<RoundButton
					onClick={() => setShowUploadExpenseCodeModal(true)}
					label="Upload CSV"
					className="secondary"
					id="upload-code"
				/>
			</div>
			{errors.expenseCodes && (
				<div className="flex justify-end px-4 pr-6 pt-1">
					<TextBodyExtraExtraSmall
						id={'expenseCodes-helper'}
						className="block pl-[16px] mt-1 text-error"
						text={(errors.expenseCodes.message as string) ?? ''}
					/>
				</div>
			)}
			<div className="text-sm mt-4 px-4">
				<p>
					Upload a list of expense codes to let your people select
					expense codes easily. They will enter codes manually in the
					meantime. If you're having trouble uploading expense
					codes, please download our template.
				</p>
			</div>
		</div>
	);

	const handleCancel = useCallback(() => {
		resetField('expenseCodes');
		setVisible(false);
	}, [resetField, setVisible]);

	const handleSave = useCallback(async () => {
		trigger('expenseCodes').then(() => {
			if (isEmpty(get(errors, 'expenseCodes'))) {
				setVisible(false);
			}
		});
	}, [trigger, errors, setVisible]);

	const handleChange = (value: string) => {
		setValue('expenseCodeType', value);
	};

	const handleKeyDown = (e: React.KeyboardEvent, value: string) => {
		if (e.key === 'Enter' || e.key === ' ') {
			handleChange(value);
		}
	};

	return (
		<>
			<SecondaryModal
				{...rest}
				draggable={false}
				header={
					<HeaderContent
						label="Expenses codes"
						icon={
							<MdReceiptLong size={30} className="text-primary" />
						}
					/>
				}
				footer={
					<FooterContent
						id="expense-code"
						onClickCancel={handleCancel}
						onClickSave={handleSave}
					/>
				}
				style={{ width: '40rem' }}
			>
				<div className="font-poppins">
					<div className="mb-6">
						<p className="text-base text-gray-700 px-2">
							Should people be required to enter expense codes
							before they request a ride?
						</p>
					</div>
					<div>
						<div
							className={
								'flex items-center justify-between py-3 h-14 border-b border-slate-300 cursor-pointer'
							}
							onClick={() => handleChange('none')}
							onKeyDown={(e) => handleKeyDown(e, 'none')}
						>
							<RadioButtonInput
								id="expenseCodeType"
								control={control}
								formName={TRAVEL_PROGRAM_FORM}
								label="No expense code required"
								value="none"
							/>
						</div>

						<div
							className={
								'flex items-center justify-between py-3 h-14 border-b border-slate-300 cursor-pointer'
							}
							onClick={() => handleChange('required')}
							onKeyDown={(e) => handleKeyDown(e, 'required')}
						>
							<RadioButtonInput
								id="expenseCodeType"
								control={control}
								formName={TRAVEL_PROGRAM_FORM}
								label="Expense code required"
								value="required"
							/>
						</div>
						{isCodeRequired && uploadExpenseCode}
					</div>
				</div>
			</SecondaryModal>
			<UploadExpenseCodeModal
				visible={showUploadExpenseCodeModal}
				setVisible={setShowUploadExpenseCodeModal}
				onHide={() => setShowUploadExpenseCodeModal(false)}
			/>
		</>
	);
};

export default ExpensesCodeModal;
